import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getCommitment,
  postCommitmentAction,
  COMMITMENT_ACTION,
} from "../../../Services/journey";
import { useStandaloneHeader, JourneyBackHeader, JourneySubpageCard } from "../shared";
import {
  activityDetail,
  commitmentIconKind,
  commitmentTiming,
  historyNewestFirst,
  progressDay,
  stepperSteps,
  MAX_STEPS,
} from "../commitmentFormat";
import { COMMITMENT_COPY } from "../copy";
import { showErrorToast } from "../../../Utils/toast";
import ModalitySheet from "../../Explore/ModalitySheet";
import openIcon from "../../../Assets/icon/journey/commitment-open.png";
import doneIcon from "../../../Assets/icon/journey/commitment-done.svg";
import walkIcon from "../../../Assets/icon/journey/commitment-walk.svg";
import trackingIcon from "../../../Assets/icon/journey/commitment-tracking.svg";
import activityDoneIcon from "../../../Assets/icon/journey/commitment-activity-done.svg";
import whyIcon from "../../../Assets/icon/journey/commitment-why.svg";
import noteIcon from "../../../Assets/icon/journey/commitment-note.svg";
import calendarIcon from "../../../Assets/icon/journey/commitment-calendar.svg";
import stepDoneIcon from "../../../Assets/icon/journey/commitment-step-done.svg";
import stepOpenIcon from "../../../Assets/icon/journey/commitment-step-open.svg";
import voiceIcon from "../../../Assets/icon/explore/sheet-voice.svg";
import "../style.css";
import "../../Explore/style.css";

const HEAD_ICON = { walk: walkIcon, open: openIcon, done: doneIcon };

/**
 * The C1 stepper: circles for up to MAX_STEPS, a bar beyond that (a daily
 * commitment over a month). Connectors into a done step are green.
 */
function ProgressStepper({ progress }) {
  const steps = stepperSteps(progress);
  if (!steps) return null;
  const target = steps.length;
  const done = steps.filter((step) => step.done).length;
  return (
    <section className="journey-cstep">
      <div className="journey-cstep__head">
        <h2 className="journey-row__title">{COMMITMENT_COPY.progressTitle}</h2>
        <p className="journey-row__sub">{COMMITMENT_COPY.progressSub(done, target)}</p>
      </div>
      {target <= MAX_STEPS ? (
        <ol className="journey-cstep__steps" style={{ "--journey-cstep-n": target }}>
          {steps.map((step) => (
            <li
              key={step.index}
              className={`journey-cstep__step${step.done ? " journey-cstep__step--done" : ""}`}
            >
              <span className="journey-cstep__circle">
                <img src={step.done ? stepDoneIcon : stepOpenIcon} alt="" width="32" height="32" />
                {step.done && <span className="journey-cstep__tick">✓</span>}
              </span>
              <span className="journey-cstep__label">
                {COMMITMENT_COPY.stepLabel(step.index, target)}
              </span>
              {step.date && <span className="journey-cstep__date">{step.date}</span>}
            </li>
          ))}
        </ol>
      ) : (
        <div className="journey-cstep__bar" aria-hidden="true">
          <span style={{ width: `${Math.round((done / target) * 100)}%` }} />
        </div>
      )}
    </section>
  );
}

const InfoRow = ({ icon, title, body }) => (
  <div className="journey-row journey-commitment__info">
    <img src={icon} alt="" width="24" height="24" />
    <span className="journey-row__text">
      <span className="journey-row__title">{title}</span>
      <span className="journey-commitment__info-body">{body}</span>
    </span>
  </div>
);

/**
 * One commitment (/journey/commitments/:commitmentId — Figma 10911:20849,
 * WP-W11 + backend gaps round 2). Also where a Your Progress event with
 * `deep_link.target: "commitment"` lands.
 *
 * Every block renders only when the payload carries it: `cadence_label` (or
 * the due-date timing line), the `progress` stepper for a recurring
 * commitment, `why_it_matters`, `aris_note`, `activity[]` (falling back to
 * `state_history` for older rows) and the `encouragement` footer. "Aris is
 * keeping track" is product copy; "Talk with Aris" opens Talk framed with
 * this commitment (`commitment_id`). Complete / Not Yet / Let It Go show
 * while it is open — on a recurring commitment Complete records one
 * occurrence and it stays open.
 */
function CommitmentDetail() {
  const { commitmentId } = useParams();
  const navigate = useNavigate();
  useStandaloneHeader();
  const [commitment, setCommitment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [talkOpen, setTalkOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const payload = await getCommitment(commitmentId);
        if (!cancelled) setCommitment(payload?.commitment || null);
      } catch (e) {
        if (e?.response?.status !== 404) console.error("Failed to load the commitment", e);
        if (!cancelled) setCommitment(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [commitmentId]);

  const act = async (action) => {
    setBusy(action);
    try {
      const payload = await postCommitmentAction(commitmentId, action);
      if (payload?.commitment) setCommitment(payload.commitment);
    } catch (e) {
      // 409 = an illegal move from the current state: the view is stale.
      console.error("Commitment action failed", e);
      showErrorToast(COMMITMENT_COPY.actionError);
      try {
        const fresh = await getCommitment(commitmentId);
        if (fresh?.commitment) setCommitment(fresh.commitment);
      } catch (_) {
        /* keep what is on screen */
      }
    } finally {
      setBusy(null);
    }
  };

  // Talk framed with this commitment (UD v26.5 C7): the id rides every chat
  // turn / the voice handshake, and the server resolves it owner-scoped.
  const onTalk = (mode) => {
    setTalkOpen(false);
    const id = encodeURIComponent(commitmentId);
    navigate(mode === "voice" ? `/coach/private?commitment_id=${id}` : `/chat?commitmentId=${id}`);
  };

  if (loading || !commitment) {
    return (
      <div className="journey-page">
        <div className="journey-page__inner">
          <JourneyBackHeader to="/journey" />
          <JourneySubpageCard>
            <div className="journey-card journey-card--status">
              {loading ? "Loading…" : COMMITMENT_COPY.notFound}
            </div>
          </JourneySubpageCard>
        </div>
      </div>
    );
  }

  const timing = commitment.cadence_label || commitmentTiming(commitment);
  const activity = Array.isArray(commitment.activity)
    ? commitment.activity.filter((entry) => entry?.label)
    : [];
  const history = activity.length ? [] : historyNewestFirst(commitment.state_history);

  return (
    <div className="journey-page">
      <div className="journey-page__inner">
        <JourneyBackHeader to="/journey" />
        <JourneySubpageCard>
          <header className="journey-commitment__head">
            <span className="journey-commitment__icon">
              <img src={HEAD_ICON[commitmentIconKind(commitment)]} alt="" width="51" height="51" />
            </span>
            <div className="journey-commitment__heading">
              <p className="journey-commitment__eyebrow">{COMMITMENT_COPY.eyebrow}</p>
              <h1 className="journey-commitment__title">{commitment.text}</h1>
              {timing && <p className="journey-commitment__timing">{timing}</p>}
            </div>
          </header>

          <ProgressStepper progress={commitment.progress} />

          {commitment.is_open && (
            <div className="journey-commitment__actions">
              <button
                type="button"
                className="journey-solid-btn"
                disabled={busy !== null}
                onClick={() => act(COMMITMENT_ACTION.COMPLETE)}
              >
                {COMMITMENT_COPY.complete}
              </button>
              <div className="journey-commitment__secondary">
                <button
                  type="button"
                  className="journey-outline-btn"
                  disabled={busy !== null}
                  onClick={() => act(COMMITMENT_ACTION.DELAY)}
                >
                  {COMMITMENT_COPY.delay}
                </button>
                <button
                  type="button"
                  className="journey-outline-btn"
                  disabled={busy !== null}
                  onClick={() => act(COMMITMENT_ACTION.RELEASE)}
                >
                  {COMMITMENT_COPY.release}
                </button>
              </div>
            </div>
          )}

          <div className="journey-rows">
            {commitment.why_it_matters && (
              <InfoRow
                icon={whyIcon}
                title={COMMITMENT_COPY.whyTitle}
                body={commitment.why_it_matters}
              />
            )}
            <InfoRow
              icon={trackingIcon}
              title={COMMITMENT_COPY.trackingTitle}
              body={COMMITMENT_COPY.trackingBody}
            />
            {commitment.aris_note?.text && (
              <InfoRow
                icon={noteIcon}
                title={COMMITMENT_COPY.noteTitle}
                body={commitment.aris_note.text}
              />
            )}
            <div className="journey-row journey-commitment__talk">
              <button
                type="button"
                className="journey-solid-btn journey-commitment__talk-btn"
                onClick={() => setTalkOpen(true)}
              >
                <img src={voiceIcon} alt="" width="20" height="20" />
                {COMMITMENT_COPY.talk}
              </button>
              <p className="journey-commitment__talk-caption">{COMMITMENT_COPY.talkCaption}</p>
            </div>
          </div>

          {activity.length > 0 && (
            <section className="journey-section">
              <h2 className="journey-section__title">{COMMITMENT_COPY.recentActivity}</h2>
              <div className="journey-rows">
                {activity.map((entry, i) => (
                  <div key={`${entry.label}-${entry.completed_at}-${i}`} className="journey-row">
                    <img src={activityDoneIcon} alt="" width="32" height="32" />
                    <span className="journey-row__text">
                      <span className="journey-row__title">{entry.label}</span>
                      <span className="journey-row__sub">{activityDetail(entry)}</span>
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {history.length > 0 && (
            <section className="journey-section">
              <h2 className="journey-section__title">{COMMITMENT_COPY.recentActivity}</h2>
              <div className="journey-rows">
                {history.map((entry, i) => (
                  <div key={`${entry.state}-${entry.at}-${i}`} className="journey-row">
                    <img
                      src={entry.state === "COMPLETED" ? activityDoneIcon : openIcon}
                      alt=""
                      width="32"
                      height="32"
                    />
                    <span className="journey-row__text">
                      <span className="journey-row__title">
                        {COMMITMENT_COPY.state[entry.state] || COMMITMENT_COPY.state.UPDATED}
                      </span>
                      <span className="journey-row__sub">{progressDay(entry.at)}</span>
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {commitment.encouragement && (
            <div className="journey-commitment__footer">
              <img src={calendarIcon} alt="" width="34" height="34" />
              <p>{commitment.encouragement}</p>
            </div>
          )}
        </JourneySubpageCard>
      </div>

      <ModalitySheet
        open={talkOpen}
        title={COMMITMENT_COPY.talkSheetTitle}
        subtitle={COMMITMENT_COPY.talkSheetSubtitle}
        onChoose={onTalk}
        onClose={() => setTalkOpen(false)}
      />
    </div>
  );
}

export default CommitmentDetail;
